<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { CsEmptyState } from '../../empty-state';
import { CsIcon } from '../../icon';
import { CsInput } from '../../input';
import {
  CsPopover,
  CsPopoverCard,
  CsPopoverCardContent,
  CsPopoverContent,
  CsPopoverTrigger,
} from '../../popover';
import { CsText } from '../../text';
import { toolbarButtonItemRecipe } from './toolbarRecipe';
import emojisGroup from '../../../../../../shared/components/emoji/emojisGroup.json';

/**
 * @typedef {object} EmojiProps
 * @property {boolean} [isDisabled=false] - Whether the emoji picker button is disabled
 * @property {boolean} [isShowHistory=true] - Whether to show the emoji history section
 * @property {number} [maxHistory=12] - Maximum number of emojis to store in history
 * @property {('default'|'inverse')} [variant='default'] - Color variant for the icon
 */

/**
 * @type {EmojiProps}
 */
const props = defineProps({
  isDisabled: {
    type: Boolean,
    default: false,
  },
  isShowHistory: {
    type: Boolean,
    default: true,
  },
  maxHistory: {
    type: Number,
    default: 12,
  },
  variant: {
    type: String,
    default: 'default',
  },
});

const emit = defineEmits(['select']);

const search = ref('');
const debouncedSearch = ref('');
const emojis = ref([]);
const history = ref([]);
const searchInput = ref(null);

// Add debounced watcher for search
watch(
  () => search.value,
  newValue => {
    debouncedSearch.value = newValue;
  },
  { debounce: 300 }
);

const filteredEmojis = computed(() => {
  if (!debouncedSearch.value) {
    return emojis.value;
  }

  const searchTerm = debouncedSearch.value.toLowerCase();
  return emojis.value
    .map(category => ({
      ...category,
      emojis: category.emojis.filter(emoji =>
        emoji.name.toLowerCase().includes(searchTerm)
      ),
    }))
    .filter(category => category.emojis.length > 0);
});

function getCategoryEmoji(emojis) {
  return emojis[0].emoji;
}

function onSelectEmoji(payload) {
  emit('select', payload.emoji, payload);

  addToHistory(payload);
}

function onClickCategory(categoryId) {
  const element = document.getElementById(categoryId);
  element.scrollIntoView({ behavior: 'smooth' });
}

function addToHistory(emoji) {
  // Add new emoji to the beginning of the array
  history.value.unshift(emoji);

  // Keep only the most recent 12 emojis
  history.value = history.value.slice(0, props.maxHistory);
  window.localStorage.setItem('emojis-history', JSON.stringify(history.value));
}

function refreshHistory() {
  const getHistory = window.localStorage.getItem('emojis-history');
  history.value = getHistory ? JSON.parse(getHistory) || [] : [];
}

function handleOpenPopover() {
  refreshHistory();
  searchInput.value?.focus();
}

function handleClosePopover() {
  search.value = '';
  debouncedSearch.value = '';
}

onMounted(async () => {
  // Lazy load emojis
  emojis.value = emojisGroup;

  // Refresh history
  if (props.isShowHistory) {
    refreshHistory();
  }
});
</script>

<template>
  <CsPopover
    v-slot="{ close }"
    is-teleport
    placement="bottom"
    class="flex"
    @open="handleOpenPopover"
    @close="handleClosePopover"
  >
    <CsPopoverTrigger>
      <button
        v-cs-tooltip="'Emoji'"
        data-component="CsEditorToolbarEmoji"
        :class="toolbarButtonItemRecipe(props)"
        :disabled="props.isDisabled"
      >
        <CsIcon name="smiley-happy" />
      </button>
    </CsPopoverTrigger>

    <CsPopoverContent>
      <CsPopoverCard>
        <CsPopoverCardContent class="min-w-[400px]">
          <div class="p-2">
            <CsInput
              ref="searchInput"
              v-model="debouncedSearch"
              placeholder="Search"
            />
          </div>

          <CsEmptyState
            v-if="filteredEmojis.length === 0"
            icon-type="no-result-found"
            heading="No results found"
            content="Your search didn't return any results."
            class="py-8"
          />

          <div
            v-else
            class="flex flex-col px-2 py-1 gap-4 max-h-[250px] overflow-y-auto"
          >
            <div
              v-if="
                history.length > 0 && props.isShowHistory && !debouncedSearch
              "
            >
              <CsText id="history" weight="medium" class="mb-1">
                History
              </CsText>

              <div class="grid grid-cols-12 gap-0.5">
                <button
                  v-for="(emoji, index) in history"
                  :key="index"
                  v-cs-tooltip="emoji.name"
                  :class="toolbarButtonItemRecipe()"
                  @click="
                    onSelectEmoji(emoji);
                    close();
                  "
                  v-html="emoji.emoji"
                />
              </div>
            </div>

            <div v-for="category in filteredEmojis" :key="category.name">
              <CsText :id="`${category.slug}`" weight="medium" class="mb-1">
                {{ category.name }}
              </CsText>

              <div class="grid grid-cols-12 gap-0.5">
                <button
                  v-for="children in category.emojis"
                  :key="children.name"
                  v-cs-tooltip="children.name"
                  :class="toolbarButtonItemRecipe()"
                  @click="
                    onSelectEmoji(children);
                    close();
                  "
                  v-html="children.emoji"
                />
              </div>
            </div>
          </div>

          <footer
            v-if="filteredEmojis.length > 0"
            class="grid grid-cols-12 gap-0.5 p-2 border-t border-brand-border bg-brand-background-secondary rounded-b-xl"
          >
            <button
              v-if="history.length > 0 && props.isShowHistory"
              v-cs-tooltip="'History'"
              :class="toolbarButtonItemRecipe()"
              @click="onClickCategory('history')"
            >
              <CsIcon name="clock" />
            </button>
            <button
              v-for="category in emojis"
              :key="category.name"
              v-cs-tooltip="category.name"
              :class="toolbarButtonItemRecipe()"
              @click="onClickCategory(category.slug)"
            >
              {{ getCategoryEmoji(category.emojis) }}
            </button>
          </footer>
        </CsPopoverCardContent>
      </CsPopoverCard>
    </CsPopoverContent>
  </CsPopover>
</template>
